/**
 * day-clone-sheet — the sheet that clones one day's ticket-type lineup onto
 * other days of the event: one step, pick which days to copy it onto, submit.
 * The source day is seeded by the caller — the `content_copy` on that day's
 * own head in `<programme-calendar>`, whose press IS the answer to "which
 * day" — so the sheet opens on the only question the band left open. It reads
 * the real event's own ticket types off `ticketTypesState` (scoped to this
 * event by `event-form`'s own `ticketTypesEventId$` push) and dispatches
 * through the real `ticketTypesAction$`/`ticketTypesResult$` channel every
 * other ticket-type write already goes through.
 *
 * The source day's types are every SINGLE-day type filed on it (`startDayOf`,
 * ticket-types.state.ts) — a whole-event or multi-day type already applies
 * wherever it's asked to and contributes no day. Target-day candidates are
 * the event's own schedule-day range (`scheduleDays`, resolved by
 * `event-form.view.ts` the same way `ticket-type-form.view.ts`'s own
 * entry-day chips are), minus the source.
 *
 * The chain-break notice — a source-day type whose `showAfterSoldOut` points
 * outside the day being copied — renders in the summary BEFORE submit,
 * mirroring the two existing precedents for this field: `chainOptions`
 * excludes a bad candidate from the picker before commit
 * (ticket-type-form.view.ts), and the delete reconciler auto-nulls a dangling
 * pointer as a visible consequence of an action the operator took. On submit
 * the write reconciler resolves that same pointer to null for the clone — it
 * never invents a same-named target — which is why this notice has to stand
 * before the click, not as a silent post-hoc count.
 *
 * Overlay contract: no full-bleed paint layer of its own — `show()` supplies
 * the material — and its own padding and its own definite measure (`w-md
 * max-w-full`), matching the design-lab proof's own overlay contract.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { Location } from "@momo/types";
import { html } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { filter, firstValueFrom, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { locationsState } from "../../storefront/locations.state.ts";
import { eventsState } from "../events.state.ts";
import {
	daySourceGroups,
	type TicketTypeWithId,
	ticketTypesAction$,
	ticketTypesResult$,
} from "./ticket-types.state.ts";

@schmancyElement("day-clone-sheet")
export class DayCloneSheet extends LocalizeMixin(SchmancyElement) {
	/** The event whose ticket types are being cloned. */
	@property({ type: String }) eventId = "";
	/** The event's own default IANA zone — day grouping and the picker's hours
	 *  display resolve against this one zone. The actual clone write does not:
	 *  `submit()` resolves each source type's own zone fresh, per type, via
	 *  `TicketType.resolveEffectiveTimezone`, since a type carrying its own
	 *  `locationId` override sells at a different venue's zone than this one. */
	@property({ type: String }) tz = "";
	/** The event's own schedule-day range, `YYYY-MM-DD[]` — the target-day
	 *  picker's candidate set, computed by the mounter the same way
	 *  `ticket-type-form.view.ts`'s own entry-day chips are
	 *  (`Location.daysBetween` over the event's own first/last wall-clock day). */
	@property({ attribute: false }) scheduleDays: readonly string[] = [];

	/** The day being copied, `YYYY-MM-DD` — always seeded by the caller, since
	 *  the head that was pressed is what names it. */
	@property({ type: String }) sourceDay = "";
	@stateDecorator() private targetDays: string[] = [];
	@stateDecorator() private saving = false;
	@stateDecorator() private failed = false;

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private async submit(
		sourceTypes: readonly TicketTypeWithId[],
	): Promise<void> {
		if (this.saving || sourceTypes.length === 0 || this.targetDays.length === 0)
			return;
		this.saving = true;
		try {
			const requestId = crypto.randomUUID();
			const resultPromise = firstValueFrom(
				ticketTypesResult$.pipe(
					filter((r) => r.requestId === requestId),
					takeUntil(this.disconnecting),
				),
			);
			// Read fresh at submit time — mirrors TicketTypeForm.save()'s own "read
			// fresh at save time" venue resolution, so each source type's own
			// locationId override resolves under its own venue's zone rather than
			// this.tz (the event's own default, used only for the picker's grouping
			// and hours display above).
			const event = eventsState.value.find((e) => e.id === this.eventId);
			const locations = Object.fromEntries(
				locationsState.value.map((l) => [l.id, l] as const),
			);
			ticketTypesAction$.next({
				kind: "cloneDay",
				requestId,
				eventId: this.eventId,
				sourceTypes,
				targetDays: [...this.targetDays],
				event: { locationId: event?.locationId ?? "" },
				locations,
			});
			const result = await resultPromise;
			if (!result.ok) {
				this.failed = true;
				return;
			}
			this.close();
		} catch {
			this.failed = true;
		} finally {
			this.saving = false;
		}
	}

	override render() {
		const sourceTypes = daySourceGroups(this.tz).get(this.sourceDay) ?? [];
		// A day whose only types span more than one day contributes none to
		// `daySourceGroups` — there is nothing to copy FROM — so the sheet says
		// that instead of drawing a picker whose submit could only be a no-op.
		if (sourceTypes.length === 0)
			return html`
				<div class="flex w-md max-w-full flex-col gap-3 p-4">
					<schmancy-typography type="title" token="sm">
						${this.t("admin.ticketTypes.dayClone.title")}
					</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.t("admin.ticketTypes.dayClone.nothingToCopy")}
					</schmancy-typography>
					<div class="flex flex-wrap items-center gap-3 pt-1">
						<schmancy-button variant="text" ?disabled=${this.saving} @click=${() => this.close()}>
							${this.t("admin.common.cancel")}
						</schmancy-button>
					</div>
				</div>
			`;
		const sourceIds = new Set(sourceTypes.map((t) => t.id));
		// The one fact only real rows can answer: which of this day's chains
		// point at a type the source-day selection does not include. Excluded
		// from the picker before commit is the field's own established
		// precedent (chainOptions in ticket-type-form.view.ts); here there is no
		// picker to exclude it from — the pointer is already stored — so the
		// same "before commit, not after" discipline names it instead.
		const chainBreaksNames = sourceTypes
			.filter((t) => t.showAfterSoldOut && !sourceIds.has(t.showAfterSoldOut))
			.map((t) => t.name);
		const first = sourceTypes[0];
		const hours =
			first?.entryWindow != null
				? `${Location.instantToWallClock(first.entryWindow.opensAt.toDate(), this.tz).slice(11)}–${Location.instantToWallClock(first.entryWindow.closesAt.toDate(), this.tz).slice(11)}`
				: "";
		const targetCandidates = this.scheduleDays.filter(
			(d) => d !== this.sourceDay,
		);

		return html`
			<div class="flex w-md max-w-full flex-col gap-3 p-4">
				<schmancy-typography type="title" token="sm">
					${this.t("admin.ticketTypes.dayClone.title")}
				</schmancy-typography>

				<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
					${this.t("admin.ticketTypes.dayClone.summaryLine", {
						count: sourceTypes.length,
						names: sourceTypes.map((t) => t.name).join(", "),
						hours,
						tz: this.tz,
					})}
				</schmancy-typography>
				${when(
					chainBreaksNames.length > 0,
					() => html`
						<schmancy-typography type="body" token="sm" class="text-error-default">
							${this.t(
								chainBreaksNames.length === 1
									? "admin.ticketTypes.dayClone.chainBreakWarningOne"
									: "admin.ticketTypes.dayClone.chainBreakWarningOther",
								{ names: chainBreaksNames.join(", ") },
							)}
						</schmancy-typography>
					`,
				)}

				<schmancy-typography type="label" token="sm" class="pt-1 text-surface-onVariant">
					${this.t("admin.ticketTypes.dayClone.targetDaysLabel")}
				</schmancy-typography>
				<schmancy-chips
					?inert=${this.saving}
					class="w-full"
					multi
					wrap
					.value=${this.targetDays}
					@change=${(e: CustomEvent<{ value: string[] }>) => {
						this.targetDays = [...e.detail.value];
					}}
				>
					${repeat(
						targetCandidates,
						(day) => day,
						(day) => {
							const d = new Date(`${day}T12:00:00.000Z`);
							const part = (opts: Intl.DateTimeFormatOptions) =>
								d.toLocaleDateString(this.locale, {
									...opts,
									timeZone: "UTC",
								});
							return html`
								<schmancy-filter-chip
									size="lg"
									.value=${day}
									aria-label=${d.toLocaleDateString(this.locale, {
										weekday: "long",
										day: "numeric",
										month: "long",
										timeZone: "UTC",
									})}
								>
									<span class="flex w-9 flex-col items-center py-2">
										<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest">
											${part({ weekday: "short" })}
										</schmancy-typography>
										<schmancy-typography type="title" token="lg" class="tabular-nums">
											${part({ day: "2-digit" })}
										</schmancy-typography>
										<schmancy-typography type="label" token="xs" transform="uppercase" class="tracking-widest">
											${part({ month: "short" })}
										</schmancy-typography>
									</span>
								</schmancy-filter-chip>
							`;
						},
					)}
				</schmancy-chips>

				<div class="flex flex-wrap items-center gap-3 pt-1">
					<schmancy-button
						variant="filled"
						.busy=${this.saving}
						color=${this.failed ? "error" : "primary"}
						?disabled=${this.targetDays.length === 0}
						@click=${() => this.submit(sourceTypes)}
					>
						<span style="display:grid">
							<span style=${this.failed ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.failed ? "true" : "false"}>${this.targetDays.length === 0 ? this.t("admin.ticketTypes.dayClone.targetDaysRequired") : this.t("admin.ticketTypes.dayClone.submitLabel", { count: this.targetDays.length * sourceTypes.length })}</span>
							<span style=${this.failed ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.failed ? "false" : "true"}>${this.t("admin.common.retry")}</span>
						</span>
					</schmancy-button>
					<schmancy-button variant="text" ?disabled=${this.saving} @click=${() => this.close()}>
						${this.t("admin.common.cancel")}
					</schmancy-button>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"day-clone-sheet": DayCloneSheet;
	}
}
